/* eslint-disable consistent-return */
import React, {
	useEffect, useState, useCallback, useMemo
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { usePrevious } from '../../../shared/utility';

const ONE_SECOND = 1000;
const ONE_MINUTE = ONE_SECOND * 60;
const ONE_HOUR = ONE_MINUTE * 60;

const getSafeBreakTimerData = () => {
	const item = window.localStorage.getItem('breakTimer');

	if (item) {
		return JSON.parse(item);
	}

	return {
		startedAt: Date.now(),
		endedAt: null
	};
};

const shouldResetTimer = (previousStatus, currentStatus) => previousStatus && currentStatus
	&& ((previousStatus === 'PREPAUSED' && currentStatus === 'PAUSED')
		|| (previousStatus === 'PAUSED' && currentStatus === 'PREPAUSED'));


const BreakStatusTimer = ({
	className = '', endedAt, isTimerEnded, status
}) => {
	const [breakStatusTime, setBreakStatusTime] = useState('--');
	const [isYellow, setIsYellow] = useState(false);
	const previousStatus = usePrevious(status);
	const { endedAt: breakTimerEndedAt, startedAt: breakTimerStartedAt } = useMemo(() => {
		if (shouldResetTimer(previousStatus, status)) {
			const newData = { startedAt: Date.now(), endedAt: null };
			window.localStorage.setItem('breakTimer', JSON.stringify(newData));
			return newData;
		}

		return getSafeBreakTimerData();
	}, [status, previousStatus]);

	const timer = endedAt ? breakTimerEndedAt : breakTimerStartedAt;

	const getBreakStatusTime = useCallback((breakStatusDate) => {
		const timeDifference = Math.abs(Date.now() - breakStatusDate);

		const restHours = timeDifference % ONE_HOUR;
		const restMinutes = restHours % ONE_MINUTE;

		const hours = Math.floor(timeDifference / ONE_HOUR).toString().padStart(2, '0');
		const minutes = Math.floor(restHours / ONE_MINUTE).toString().padStart(2, '0');
		const seconds = Math.floor(restMinutes / ONE_SECOND).toString().padStart(2, '0');

		if (hours > 0) {
			setBreakStatusTime(`${hours}h ${minutes}m ${seconds}s`);
		} else if (minutes > 0) {
			setBreakStatusTime(`${minutes}m ${seconds}s`);
		} else {
			setBreakStatusTime(`${seconds}s`);
		}

		if (endedAt && timeDifference < ONE_MINUTE) {
			setIsYellow(true);
		} else {
			setIsYellow(false);
		}
	}, [endedAt]);

	useEffect(() => {
		if (endedAt && isTimerEnded) {
			setBreakStatusTime('00:00');
		} else if (!endedAt || !isTimerEnded) {
			if (timer) {
				getBreakStatusTime(timer);

				const interval = setInterval(() => {
					getBreakStatusTime(timer);
				}, ONE_SECOND);

				return () => clearInterval(interval);
			}
		}
	}, [timer, endedAt, isTimerEnded, getBreakStatusTime, setBreakStatusTime]);

	return <span className={(isYellow && 'color-yellow') || (breakStatusTime === '00:00' && 'color-red') || className}>{breakStatusTime}</span>;
};

const mapStateToProps = ({ agent }) => ({
	agentBreakStatus: agent.agentBreakStatus
});

BreakStatusTimer.propTypes = {
	className: PropTypes.string,
	agentBreakStatus: PropTypes.shape({
		startedAt: PropTypes.number
	}).isRequired,
	endedAt: PropTypes.bool,
	isTimerEnded: PropTypes.bool,
	status: PropTypes.string
};

export default connect(mapStateToProps)(BreakStatusTimer);
